import { useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Undo2, Upload as UploadIcon } from 'lucide-react'

import { Link } from '@/lib/router'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip, ChipGroup } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Kpi } from '@/components/ui/kpi'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { PageSummary, PagerButtons } from '@/features/cases/Pager'
import { dateTime, humanizeCode, inrFull, num } from '@/lib/format'

import {
  OUTCOME_LABELS,
  lmsReconErrorMessage,
  useLmsBatch,
  useLmsBatchRows,
  useLmsBatches,
  useLmsExceptions,
  useRevertLmsBatch,
  useUnreconciled,
  useUploadLmsBatch,
  type LmsBatch,
  type LmsBatchDetail,
  type LmsOutcome,
} from './lmsApi'

const UNRECONCILED_PAGE = 50
const BATCH_PAGE = 20
const ROW_PAGE = 100

const OUTCOME_TONE: Record<LmsOutcome, 'positive' | 'slate' | 'warning' | 'negative'> = {
  RECONCILED: 'positive',
  ALREADY_RECONCILED: 'slate',
  DUPLICATE_IN_FILE: 'slate',
  NOT_CONFIRMED: 'warning',
  NOT_FOUND: 'negative',
  OUT_OF_SCOPE: 'negative',
  AMOUNT_MISMATCH: 'negative',
  INVALID: 'negative',
}

/**
 * LMS posting: ops post the repayments export into the LMS, then upload the
 * same file here to mark its rows reconciled. What is still owed, every batch
 * with its row-by-row outcome, and reconciled money that has since stopped
 * being confirmed.
 */
export function LmsPostingTab() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(0)
  const [batchPage, setBatchPage] = useState(0)
  const [openBatch, setOpenBatch] = useState<string | null>(null)

  const filters = { from: from || undefined, to: to || undefined }
  const unreconciled = useUnreconciled(filters, page, UNRECONCILED_PAGE)
  const exceptions = useLmsExceptions()
  const batches = useLmsBatches(batchPage, BATCH_PAGE)

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <Kpi
          label="Not reconciled"
          value={unreconciled.data ? num(unreconciled.data.total) : '—'}
          hint={from || to ? 'Confirmed collections in the dates below' : 'Confirmed collections, all time'}
          loading={unreconciled.isPending}
          deltaSense="lower-is-better"
        />
        <Kpi
          label="Amount not reconciled"
          value={unreconciled.data ? inrFull(unreconciled.data.totalAmount) : '—'}
          hint="Not yet marked posted in the LMS"
          loading={unreconciled.isPending}
          deltaSense="lower-is-better"
        />
        <Kpi
          label="Exceptions"
          value={exceptions.data ? num(exceptions.data.length) : '—'}
          hint="Reconciled, then no longer confirmed"
          loading={exceptions.isPending}
          deltaSense="lower-is-better"
        />
      </div>

      <UploadCard onOpenBatch={(id) => { setBatchPage(0); setOpenBatch(id) }} />

      {exceptions.data && exceptions.data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Exceptions</CardTitle>
            <CardDescription>
              Reconciled collections that were later cancelled, found short or reversed. Each is still
              posted in the LMS and needs reversing there.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-2xs uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Receipt No</th>
                    <th className="py-2 pr-3">Loan</th>
                    <th className="py-2 pr-3">Customer</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2 pr-3">Now</th>
                    <th className="py-2 pr-3">Batch</th>
                    <th className="py-2 pr-3">Collected</th>
                  </tr>
                </thead>
                <tbody>
                  {exceptions.data.map((c) => (
                    <tr key={c.collectionId} className="border-b border-slate-100">
                      <td className="py-1.5 pr-3 font-mono text-xs">{c.receiptNo}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs">{c.loanAccountNo}</td>
                      <td className="py-1.5 pr-3">{c.customerName ?? '—'}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{inrFull(c.amount)}</td>
                      <td className="py-1.5 pr-3"><Badge tone="negative">{humanizeCode(c.status)}</Badge></td>
                      <td className="py-1.5 pr-3 tabular-nums">{c.batchNo}</td>
                      <td className="py-1.5 pr-3 tabular-nums">{dateTime(c.collectedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <CardTitle>Not reconciled</CardTitle>
              <CardDescription>
                Confirmed collections no upload has marked as posted, oldest first. Pending cash is left
                out until its deposit is confirmed.
              </CardDescription>
            </div>
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label htmlFor="lms-from">From</Label>
                <Input id="lms-from" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0) }} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lms-to">To</Label>
                <Input id="lms-to" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0) }} />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {unreconciled.isPending ? (
            <SkeletonRows rows={5} />
          ) : unreconciled.isError ? (
            <p className="px-5 pb-4 text-sm text-negative">
              {lmsReconErrorMessage(unreconciled.error, "Couldn't load the collections.")}
            </p>
          ) : unreconciled.data.items.length === 0 ? (
            <p className="px-5 py-6 text-center text-sm text-slate-500">
              Every confirmed collection{from || to ? ' in these dates' : ''} is reconciled.
            </p>
          ) : (
            <div className="overflow-x-auto px-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-2xs uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Receipt No</th>
                    <th className="py-2 pr-3">Loan</th>
                    <th className="py-2 pr-3">Customer</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2 pr-3">Collected</th>
                    <th className="py-2 pr-3">Mode</th>
                    <th className="py-2 pr-3">Collected by</th>
                    <th className="py-2 pr-3">Branch</th>
                  </tr>
                </thead>
                <tbody>
                  {unreconciled.data.items.map((c) => (
                    <tr key={c.collectionId} className="border-b border-slate-100">
                      <td className="py-1.5 pr-3 font-mono text-xs">{c.receiptNo}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs">{c.loanAccountNo}</td>
                      <td className="py-1.5 pr-3">{c.customerName ?? '—'}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{inrFull(c.amount)}</td>
                      <td className="py-1.5 pr-3 tabular-nums">{dateTime(c.collectedAt)}</td>
                      <td className="py-1.5 pr-3">{humanizeCode(c.mode)}</td>
                      <td className="py-1.5 pr-3">{c.agentName ?? c.empId ?? '—'}</td>
                      <td className="py-1.5 pr-3">{c.branchCode ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {unreconciled.data && unreconciled.data.total > 0 && (
            <Pager
              page={page}
              setPage={setPage}
              pageSize={UNRECONCILED_PAGE}
              rowsOnPage={unreconciled.data.items.length}
              total={unreconciled.data.total}
              loading={unreconciled.isPlaceholderData}
              emptyLabel="No collections"
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Uploads</CardTitle>
          <CardDescription>
            Every upload, newest first. Open one to see what happened to each row of the file, or to
            revert it.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {batches.isPending ? (
            <SkeletonRows rows={4} />
          ) : batches.isError ? (
            <p className="px-5 pb-4 text-sm text-negative">
              {lmsReconErrorMessage(batches.error, "Couldn't load the uploads.")}
            </p>
          ) : batches.data.items.length === 0 ? (
            <p className="px-5 py-6 text-center text-sm text-slate-500">Nothing uploaded yet.</p>
          ) : (
            <div className="overflow-x-auto px-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-2xs uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Batch</th>
                    <th className="py-2 pr-3">File</th>
                    <th className="py-2 pr-3">Uploaded</th>
                    <th className="py-2 pr-3 text-right">Rows</th>
                    <th className="py-2 pr-3 text-right">Reconciled</th>
                    <th className="py-2 pr-3 text-right">Already</th>
                    <th className="py-2 pr-3 text-right">Rejected</th>
                    <th className="py-2 pr-3" />
                  </tr>
                </thead>
                <tbody>
                  {batches.data.items.map((b) => (
                    <BatchRows
                      key={b.batchId}
                      batch={b}
                      open={openBatch === b.batchId}
                      onToggle={() => setOpenBatch(openBatch === b.batchId ? null : b.batchId)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {batches.data && batches.data.total > BATCH_PAGE && (
            <Pager
              page={batchPage}
              setPage={setBatchPage}
              pageSize={BATCH_PAGE}
              rowsOnPage={batches.data.items.length}
              total={batches.data.total}
              loading={batches.isPlaceholderData}
              emptyLabel="No uploads"
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function UploadCard({ onOpenBatch }: { onOpenBatch: (batchId: string) => void }) {
  const [file, setFile] = useState<File | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const upload = useUploadLmsBatch()

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!file) return
    upload.mutate(file, {
      onSuccess: () => {
        setFile(null)
        if (fileRef.current) fileRef.current.value = ''
      },
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload posted collections</CardTitle>
        <CardDescription>
          Download the repayments file from{' '}
          <Link to="/collections" className="text-navy-700 underline-offset-4 hover:underline">Collections</Link>,
          post it in the LMS, then upload the rows you posted here. Matching uses Receipt No, LOAN NO and
          Amount; leave those columns as they were exported.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {upload.isSuccess && <UploadResult result={upload.data} onOpen={() => onOpenBatch(upload.data.batch.batchId)} />}
        {upload.isError && (
          <Alert variant="destructive">
            <AlertTitle>Upload refused</AlertTitle>
            <AlertDescription>{lmsReconErrorMessage(upload.error, 'The upload failed. Try again.')}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={onSubmit} className="grid items-end gap-3 sm:grid-cols-[1fr_auto]">
          <div className="space-y-1.5">
            <Label htmlFor="lms-file">Repayments file</Label>
            <Input
              id="lms-file"
              ref={fileRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="cursor-pointer"
            />
          </div>
          <Button type="submit" disabled={!file || upload.isPending} className="justify-self-end">
            <UploadIcon className="h-3.5 w-3.5" />
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function UploadResult({ result, onOpen }: { result: LmsBatchDetail; onOpen: () => void }) {
  const b = result.batch
  return (
    <Alert variant={b.rejectedCount > 0 ? 'warning' : 'positive'}>
      <AlertTitle>Batch {b.batchNo} uploaded</AlertTitle>
      <AlertDescription>
        <span className="block">
          {num(b.reconciledCount)} reconciled ({inrFull(b.reconciledAmount)})
          {b.alreadyCount > 0 && ` · ${num(b.alreadyCount)} already reconciled`}
          {b.rejectedCount > 0 && ` · ${num(b.rejectedCount)} rejected`}
          {' '}of {num(b.rowsInFile)} rows.
        </span>
        <button type="button" onClick={onOpen} className="mt-1 text-navy-700 underline-offset-4 hover:underline">
          See every row
        </button>
      </AlertDescription>
    </Alert>
  )
}

function BatchRows({ batch: b, open, onToggle }: { batch: LmsBatch; open: boolean; onToggle: () => void }) {
  const reverted = b.status === 'REVERTED'
  return (
    <>
      <tr className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" onClick={onToggle}>
        <td className="py-1.5 pr-3 tabular-nums">
          <span className="inline-flex items-center gap-1">
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {b.batchNo}
          </span>
        </td>
        <td className="max-w-[220px] truncate py-1.5 pr-3" title={b.fileName}>{b.fileName}</td>
        <td className="py-1.5 pr-3">
          <span className="tabular-nums">{dateTime(b.uploadedAt)}</span>
          <span className="block text-2xs text-muted-foreground">{b.uploadedByName} · {b.uploadedByEmpId}</span>
        </td>
        <td className="py-1.5 pr-3 text-right tabular-nums">{num(b.rowsInFile)}</td>
        <td className="py-1.5 pr-3 text-right tabular-nums">
          {num(b.reconciledCount)}
          <span className="block text-2xs text-muted-foreground">{inrFull(b.reconciledAmount)}</span>
        </td>
        <td className="py-1.5 pr-3 text-right tabular-nums">{num(b.alreadyCount)}</td>
        <td className={`py-1.5 pr-3 text-right tabular-nums ${b.rejectedCount > 0 ? 'text-negative' : ''}`}>
          {num(b.rejectedCount)}
        </td>
        <td className="py-1.5 pr-3">{reverted && <Badge tone="warning">Reverted</Badge>}</td>
      </tr>
      {open && (
        <tr className="border-b border-slate-100 bg-slate-50/60">
          <td colSpan={8} className="px-3 py-3">
            <BatchPanel batchId={b.batchId} />
          </td>
        </tr>
      )}
    </>
  )
}

function BatchPanel({ batchId }: { batchId: string }) {
  const [outcome, setOutcome] = useState<LmsOutcome | null>(null)
  const [page, setPage] = useState(0)
  const detail = useLmsBatch(batchId)
  const rows = useLmsBatchRows(batchId, outcome, page, ROW_PAGE)

  if (detail.isPending) return <SkeletonRows rows={3} />
  if (detail.isError) {
    return <p className="text-sm text-negative">{lmsReconErrorMessage(detail.error, "Couldn't load this batch.")}</p>
  }
  const b = detail.data.batch
  const counts = Object.entries(detail.data.outcomes) as [LmsOutcome, number][]

  return (
    <div className="space-y-3">
      {b.status === 'REVERTED' ? (
        <p className="text-sm text-navy-900">
          Reverted {dateTime(b.revertedAt)} by {b.revertedByName} ({b.revertedByEmpId}): “{b.revertReason}”.
          Its collections are back in Not reconciled.
        </p>
      ) : (
        <RevertForm batch={b} />
      )}

      <ChipGroup>
        <Chip active={outcome === null} onClick={() => { setOutcome(null); setPage(0) }}>
          All · {num(b.rowsInFile)}
        </Chip>
        {counts.map(([o, n]) => (
          <Chip key={o} active={outcome === o} onClick={() => { setOutcome(o); setPage(0) }}>
            {OUTCOME_LABELS[o]} · {num(n)}
          </Chip>
        ))}
      </ChipGroup>

      {rows.data && (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-2xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Row</th>
                <th className="py-2 pr-3">Receipt No</th>
                <th className="py-2 pr-3">LOAN NO</th>
                <th className="py-2 pr-3 text-right">Amount</th>
                <th className="py-2 pr-3">Outcome</th>
                <th className="py-2 pr-3">Why</th>
              </tr>
            </thead>
            <tbody>
              {rows.data.items.map((r) => (
                <tr key={r.rowNumber} className="border-b border-slate-100 last:border-0">
                  <td className="px-3 py-1.5 tabular-nums">{r.rowNumber}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{r.receiptNo ?? '—'}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{r.loanAccountNo ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{r.amount === null ? '—' : inrFull(r.amount)}</td>
                  <td className="py-1.5 pr-3"><Badge tone={OUTCOME_TONE[r.outcome]}>{OUTCOME_LABELS[r.outcome]}</Badge></td>
                  <td className="py-1.5 pr-3 text-xs text-muted-foreground">{r.detail ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.data.total > ROW_PAGE && (
            <Pager
              page={page}
              setPage={setPage}
              pageSize={ROW_PAGE}
              rowsOnPage={rows.data.items.length}
              total={rows.data.total}
              loading={rows.isPlaceholderData}
              emptyLabel="No rows"
            />
          )}
        </div>
      )}
    </div>
  )
}

function RevertForm({ batch: b }: { batch: LmsBatch }) {
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')
  const revert = useRevertLmsBatch()

  if (!asking) {
    return (
      <Button variant="outline" size="sm" onClick={() => setAsking(true)}>
        <Undo2 className="h-3.5 w-3.5" /> Revert batch {b.batchNo}
      </Button>
    )
  }
  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3">
      <p className="text-sm text-navy-900">
        The {num(b.reconciledCount)} collections this batch reconciled go back to Not reconciled. The batch
        and its rows stay here, marked reverted.
      </p>
      <Label htmlFor={`revert-${b.batchId}`}>Why is it being reverted?</Label>
      <Textarea
        id={`revert-${b.batchId}`}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={500}
        rows={2}
      />
      {revert.isError && (
        <p className="text-xs text-negative">{lmsReconErrorMessage(revert.error, 'The revert failed. Try again.')}</p>
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!reason.trim() || revert.isPending}
          onClick={() => revert.mutate({ batchId: b.batchId, reason: reason.trim() })}
        >
          {revert.isPending ? 'Reverting…' : `Revert batch ${b.batchNo}`}
        </Button>
        <Button size="sm" variant="ghost" disabled={revert.isPending} onClick={() => setAsking(false)}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

function Pager({
  page, setPage, pageSize, rowsOnPage, total, loading, emptyLabel,
}: {
  page: number
  setPage: (update: (p: number) => number) => void
  pageSize: number
  rowsOnPage: number
  total: number
  loading: boolean
  emptyLabel: string
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3">
      <span className="text-2xs uppercase tracking-wider text-muted-foreground">
        {loading ? <>Loading page {page + 1}…</> : (
          <PageSummary page={page} rowsOnPage={rowsOnPage} total={total} pageSize={pageSize} emptyLabel={emptyLabel} />
        )}
      </span>
      <span className={loading ? 'pointer-events-none inline-flex gap-4 opacity-50' : 'inline-flex gap-4'}>
        <PagerButtons page={page} setPage={setPage} rowsOnPage={rowsOnPage} total={total} pageSize={pageSize} />
      </span>
    </div>
  )
}
